// The glossary of synthesizer terms for beginners (/docs/glossary), shared by every product's documentation, which
// links to an entry as /docs/glossary#term-{id}. Plain text: the page adds the links. ids are fixed once published
// (other pages link to them); a translation keeps them and translates term, aliases and definition. Only words the
// products' documentation uses, explained without assuming any other entry has been read.

export interface GlossaryEntry {
    id: string;
    term: string;
    // Other names it goes by, which the filter also finds.
    aliases?: string[];
    definition: string;
    seeAlso?: string[];
}

export const glossary: GlossaryEntry[] = [
    {
        id: 'aftertouch',
        term: 'Aftertouch',
        aliases: ['pressure', 'channel pressure', 'polyphonic aftertouch'],
        definition: "How hard you keep pressing a key after it's down. Keyboards that send it let you, for example, add vibrato or open the filter by leaning into a note you're holding. Channel aftertouch is one value for the whole keyboard; polyphonic aftertouch is a value for each key.",
        seeAlso: ['velocity', 'mpe', 'modulation']
    },
    {
        id: 'aliasing',
        term: 'Aliasing',
        definition: "Harsh, out-of-tune tones that don't belong to the sound, made when a synthesizer produces frequencies too high for the sample rate to hold. They're most audible on very bright sounds played high up the keyboard. Oversampling reduces them.",
        seeAlso: ['oversampling', 'sample-rate']
    },
    {
        id: 'amplifier',
        term: 'Amplifier',
        aliases: ['VCA', 'amp'],
        definition: "The stage that sets how loud a voice is at each moment. An envelope usually controls it, so a note can fade in, hold, and fade out instead of switching on and off. On analog synthesizers it was a voltage-controlled amplifier, or VCA, and the name stuck.",
        seeAlso: ['envelope', 'adsr', 'voice']
    },
    {
        id: 'arpeggiator',
        term: 'Arpeggiator',
        aliases: ['arp'],
        definition: "Plays the notes you're holding one after another, in a pattern (up, down, up and down, or in the order you pressed them), in time with the tempo. Hold a chord and it becomes a rhythmic figure. Most arpeggiators can also repeat the pattern over more than one octave.",
        seeAlso: ['tempo-sync', 'step-sequencer', 'gate']
    },
    {
        id: 'attack',
        term: 'Attack',
        definition: "The first stage of an envelope: how long it takes to rise from nothing to its peak after a key goes down. A short attack gives a sharp, percussive start; a long one makes the sound swell in, as with strings or pads.",
        seeAlso: ['adsr', 'decay', 'release']
    },
    {
        id: 'au',
        term: 'Audio Unit',
        aliases: ['AU', 'AUv2'],
        definition: "Apple's plug-in format, used by Logic Pro, GarageBand and other macOS apps. It's one of the formats an instrument can be installed as, alongside VST3 and AAX.",
        seeAlso: ['plug-in', 'vst3', 'aax', 'daw']
    },
    {
        id: 'aax',
        term: 'AAX',
        definition: "Avid's plug-in format, the one Pro Tools uses.",
        seeAlso: ['plug-in', 'au', 'vst3']
    },
    {
        id: 'band-pass',
        term: 'Band-pass filter',
        aliases: ['BP', 'bandpass'],
        definition: "A filter that lets through a band of frequencies around the cutoff and takes away both what's below and what's above it. It sounds thin, nasal or telephone-like, and is good for sounds that need to sit in a busy mix without taking up much room.",
        seeAlso: ['filter', 'cutoff', 'low-pass', 'high-pass', 'notch']
    },
    {
        id: 'bank',
        term: 'Bank',
        definition: "A collection of presets. The factory bank is the set that comes with the instrument; you can keep your own presets alongside it.",
        seeAlso: ['preset']
    },
    {
        id: 'bipolar',
        term: 'Bipolar',
        aliases: ['unipolar'],
        definition: "A control or modulation that can go both ways from zero: down as well as up. A bipolar amount of −50 pushes a destination down as far as +50 pushes it up, and 0, in the middle, does nothing. A unipolar one only goes up from zero, like the mod wheel or velocity.",
        seeAlso: ['modulation', 'source', 'modulation-matrix']
    },
    {
        id: 'bpm',
        term: 'BPM',
        aliases: ['tempo', 'beats per minute'],
        definition: "Beats per minute: the speed of the music. Arpeggiators, sequencers, delays and synced LFOs follow it, so they stay in time with the song.",
        seeAlso: ['tempo-sync', 'clock']
    },
    {
        id: 'buffer-size',
        term: 'Buffer size',
        aliases: ['block size', 'audio buffer'],
        definition: "How much audio the computer works out at a time, in samples. Smaller buffers mean less delay between pressing a key and hearing it (latency), but give the computer less time, so too small a buffer causes clicks and dropouts. 128 or 256 samples is a common starting point; raise it if you hear crackles.",
        seeAlso: ['latency', 'sample-rate']
    },
    {
        id: 'cent',
        term: 'Cent',
        definition: "A hundredth of a semitone, the unit for fine tuning. A few cents of difference between two oscillators makes them beat slowly against each other, which thickens the sound.",
        seeAlso: ['semitone', 'detune', 'octave']
    },
    {
        id: 'chorus',
        term: 'Chorus',
        definition: "An effect that mixes the sound with slightly delayed copies whose delay slowly wavers, so it sounds like several instruments playing together. It makes sounds wider and richer, and is a classic on pads, strings and electric pianos.",
        seeAlso: ['effects', 'flanger', 'phaser', 'dry-wet']
    },
    {
        id: 'clock',
        term: 'Clock',
        definition: "The pulse an arpeggiator or sequencer steps along to. It usually follows your DAW's tempo; in the standalone app, the app's own tempo.",
        seeAlso: ['bpm', 'tempo-sync', 'arpeggiator', 'step-sequencer']
    },
    {
        id: 'cc',
        term: 'Control change',
        aliases: ['CC', 'MIDI CC', 'controller'],
        definition: "A kind of MIDI message that carries the position of a knob, slider, pedal or wheel, as a number from 0 to 127 on one of 128 numbered controllers. The mod wheel is CC 1 and the sustain pedal CC 64, for example. MIDI Learn ties a control on screen to the CC your hardware sends.",
        seeAlso: ['midi', 'midi-learn', 'mod-wheel']
    },
    {
        id: 'cutoff',
        term: 'Cutoff',
        aliases: ['cutoff frequency', 'frequency'],
        definition: "The frequency where a filter starts taking sound away. On a low-pass filter, turning the cutoff down makes the sound darker and more muffled; turning it up makes it brighter. Moving it while a note plays, by hand or with an envelope or LFO, is the classic synthesizer sweep.",
        seeAlso: ['filter', 'resonance', 'key-tracking', 'envelope']
    },
    {
        id: 'daw',
        term: 'DAW',
        aliases: ['digital audio workstation', 'host'],
        definition: "Digital audio workstation: the app you record and arrange music in, such as Logic Pro, Ableton Live, Cubase, FL Studio, Reaper or Pro Tools. Instruments run inside it as plug-ins. Plug-in documentation sometimes calls it the host.",
        seeAlso: ['plug-in', 'standalone']
    },
    {
        id: 'decay',
        term: 'Decay',
        definition: "The second stage of an envelope: how long it takes to fall from its peak to the sustain level while the key is held. With sustain at zero, the decay alone shapes a plucked or struck sound.",
        seeAlso: ['adsr', 'attack', 'sustain']
    },
    {
        id: 'delay',
        term: 'Delay',
        aliases: ['echo'],
        definition: "An effect that repeats the sound after a set time, like an echo. Feedback sets how many repeats there are, and the time can follow the tempo so the echoes land on the beat.",
        seeAlso: ['effects', 'reverb', 'tempo-sync', 'dry-wet']
    },
    {
        id: 'destination',
        term: 'Destination',
        aliases: ['target'],
        definition: "What a modulation changes: the pitch, the filter's cutoff, the volume, an LFO's rate, and so on. In a modulation matrix, each route has a source, a destination and an amount.",
        seeAlso: ['modulation-matrix', 'source', 'modulation']
    },
    {
        id: 'detune',
        term: 'Detune',
        definition: "Tuning oscillators, or unison voices, slightly apart. The small differences make them drift in and out of step with each other, which sounds thicker, wider and more alive than a single oscillator.",
        seeAlso: ['cent', 'unison', 'oscillator']
    },
    {
        id: 'distortion',
        term: 'Distortion',
        aliases: ['drive', 'overdrive', 'saturation'],
        definition: "Pushing a sound harder than a circuit can cleanly pass, which rounds off or clips its peaks and adds new harmonics. A little (saturation, drive) adds warmth and grit; a lot sounds fuzzy and aggressive.",
        seeAlso: ['effects', 'harmonics']
    },
    {
        id: 'dry-wet',
        term: 'Dry/wet',
        aliases: ['mix', 'dry', 'wet'],
        definition: "How much of an effect you hear. Dry is the sound without the effect, wet the effect's output. A mix of 0% is all dry, 100% all wet; most effects sound best somewhere between.",
        seeAlso: ['effects']
    },
    {
        id: 'effects',
        term: 'Effects',
        aliases: ['FX'],
        definition: "Processing applied after the voices are mixed together, such as chorus, delay, reverb or distortion. They change the sound's space and color rather than its notes.",
        seeAlso: ['chorus', 'delay', 'reverb', 'distortion', 'dry-wet']
    },
    {
        id: 'envelope',
        term: 'Envelope',
        aliases: ['EG', 'envelope generator', 'contour'],
        definition: "A shape that plays out once each time a key goes down and finishes after it comes up. Applied to volume, it decides whether a note starts sharply or swells, and whether it stops at once or fades. Applied to the filter or pitch, it makes the tone change over the note, like the bright attack of a plucked string.",
        seeAlso: ['adsr', 'amplifier', 'modulation', 'lfo']
    },
    {
        id: 'adsr',
        term: 'ADSR',
        definition: "The four stages of the most common envelope: attack (rising to the peak), decay (falling to the sustain level), sustain (the level held while the key is down), and release (fading after the key comes up). Attack, decay and release are times; sustain is a level.",
        seeAlso: ['envelope', 'attack', 'decay', 'sustain', 'release']
    },
    {
        id: 'filter',
        term: 'Filter',
        aliases: ['VCF'],
        definition: "Takes away part of the sound's frequencies to change its tone, the way the bass and treble controls on a stereo do, but much more strongly. Its cutoff sets where it acts and its resonance how sharply. Most synthesizer sounds get their character from the filter.",
        seeAlso: ['cutoff', 'resonance', 'low-pass', 'high-pass', 'band-pass', 'slope']
    },
    {
        id: 'flanger',
        term: 'Flanger',
        definition: "An effect that mixes the sound with a copy delayed by a very short, sweeping time, giving a whooshing, jet-plane sweep. It's like a stronger, more metallic chorus.",
        seeAlso: ['effects', 'chorus', 'phaser']
    },
    {
        id: 'formant',
        term: 'Formant',
        aliases: ['formants', 'vowel filter'],
        definition: "A peak of loudness at a fixed frequency that gives a sound its character, whatever note is played. The formants of your voice are what make an \"ah\" sound different from an \"ee\". Synthesizers imitate them with two or more filter bands at once, for vowel-like and talking sounds.",
        seeAlso: ['filter', 'band-pass', 'resonance']
    },
    {
        id: 'gate',
        term: 'Gate',
        aliases: ['gate length', 'note length'],
        definition: "How long a key, or a step in a sequence, counts as held. In an arpeggiator or sequencer, the gate length sets how much of each step the note lasts: short for staccato, long for notes that run into each other.",
        seeAlso: ['arpeggiator', 'step-sequencer', 'legato']
    },
    {
        id: 'glide',
        term: 'Glide',
        aliases: ['portamento'],
        definition: "Sliding smoothly from one note's pitch to the next instead of jumping. The time sets how long the slide takes. It's a signature of lead and bass sounds, especially played legato.",
        seeAlso: ['legato', 'monophonic']
    },
    {
        id: 'harmonics',
        term: 'Harmonics',
        aliases: ['overtones', 'partials'],
        definition: "The higher frequencies above a note's pitch that give it its tone. A sine wave has none; a sawtooth has all of them, which is why it sounds bright and buzzy. Filters work by taking harmonics away.",
        seeAlso: ['waveform', 'filter']
    },
    {
        id: 'high-pass',
        term: 'High-pass filter',
        aliases: ['HP', 'highpass', 'low-cut'],
        definition: "A filter that takes away the frequencies below the cutoff and lets the higher ones through. It thins a sound out, and is useful for keeping a pad or lead from clashing with the bass.",
        seeAlso: ['filter', 'cutoff', 'low-pass', 'band-pass']
    },
    {
        id: 'init',
        term: 'Init patch',
        aliases: ['Init', 'initialize', 'default patch'],
        definition: "A plain starting sound with everything set to simple defaults, usually one oscillator straight through an open filter. Starting from Init is the best way to learn: change one thing at a time and listen to what it does.",
        seeAlso: ['preset']
    },
    {
        id: 'key-tracking',
        term: 'Key tracking',
        aliases: ['keyboard tracking', 'key follow', 'kbd track'],
        definition: "Making something follow the note you play. Filter key tracking raises the cutoff as you play higher, so high notes aren't duller than low ones. At 100%, the cutoff moves exactly with the pitch.",
        seeAlso: ['cutoff', 'filter', 'modulation']
    },
    {
        id: 'latency',
        term: 'Latency',
        definition: "The delay between pressing a key and hearing the note. A few milliseconds go unnoticed; much more makes playing feel sluggish. A smaller buffer size reduces it.",
        seeAlso: ['buffer-size']
    },
    {
        id: 'legato',
        term: 'Legato',
        definition: "Playing so that each note starts before the last one ends. Many synthesizers react to it: in legato mode a new note held over the last doesn't restart the envelopes, and glide may only happen between legato notes.",
        seeAlso: ['glide', 'monophonic', 'envelope']
    },
    {
        id: 'lfo',
        term: 'LFO',
        aliases: ['low-frequency oscillator'],
        definition: "Low-frequency oscillator: an oscillator too slow to hear as a note, used to move something else up and down over and over. On pitch it gives vibrato, on volume tremolo, on the filter a repeating wah. Its rate sets the speed (it can follow the tempo) and its depth or amount how far it moves things.",
        seeAlso: ['modulation', 'vibrato', 'tremolo', 'tempo-sync', 'envelope']
    },
    {
        id: 'low-pass',
        term: 'Low-pass filter',
        aliases: ['LP', 'lowpass', 'high-cut'],
        definition: "The most common synthesizer filter: it lets the frequencies below the cutoff through and takes away those above, so lowering the cutoff makes the sound darker and softer.",
        seeAlso: ['filter', 'cutoff', 'resonance', 'high-pass', 'slope']
    },
    {
        id: 'midi',
        term: 'MIDI',
        definition: "The standard way keyboards, controllers and music software talk to each other. It doesn't carry sound, only instructions: which notes start and stop, how hard they were played, and where the knobs, wheels and pedals are.",
        seeAlso: ['cc', 'velocity', 'midi-learn', 'mpe']
    },
    {
        id: 'midi-learn',
        term: 'MIDI Learn',
        definition: "A quick way to control an on-screen knob from your hardware: choose the control, move a knob or slider on your keyboard, and from then on that hardware control moves it.",
        seeAlso: ['midi', 'cc']
    },
    {
        id: 'mixer',
        term: 'Mixer',
        definition: "The stage that sets how loud each sound source (the oscillators, the noise, and so on) is before they go into the filter together. Turning a source up also drives the filter harder, which on some instruments adds a little grit.",
        seeAlso: ['oscillator', 'noise', 'filter']
    },
    {
        id: 'mod-wheel',
        term: 'Mod wheel',
        aliases: ['modulation wheel'],
        definition: "The wheel beside a keyboard's pitch bend, usually used to bring in vibrato or another effect as you play. It sends MIDI CC 1. Many instruments let you choose what it controls.",
        seeAlso: ['pitch-bend', 'cc', 'vibrato', 'modulation']
    },
    {
        id: 'modulation',
        term: 'Modulation',
        aliases: ['mod'],
        definition: "Having one thing change another automatically: an LFO wobbling the pitch, an envelope sweeping the filter, velocity making notes brighter. Modulation is what makes a synthesizer sound move and breathe instead of staying static.",
        seeAlso: ['source', 'destination', 'modulation-matrix', 'lfo', 'envelope']
    },
    {
        id: 'modulation-matrix',
        term: 'Modulation matrix',
        aliases: ['mod matrix'],
        definition: "A table of modulation routes, each connecting a source to a destination with an amount. It lets you set up connections the front panel doesn't have, such as aftertouch opening the filter or an envelope changing an LFO's speed. A negative amount moves the destination the other way.",
        seeAlso: ['modulation', 'source', 'destination']
    },
    {
        id: 'monophonic',
        term: 'Monophonic',
        aliases: ['mono'],
        definition: "Playing one note at a time. A new key takes over from the last, which suits basses and leads, and makes glide and legato playing natural.",
        seeAlso: ['polyphonic', 'legato', 'glide', 'voice']
    },
    {
        id: 'mpe',
        term: 'MPE',
        aliases: ['MIDI Polyphonic Expression'],
        definition: "MIDI Polyphonic Expression: a way for expressive controllers to send bend, pressure and slide for each note separately, so you can bend one note of a chord or swell a single note. It needs a controller that supports it.",
        seeAlso: ['midi', 'aftertouch', 'pitch-bend']
    },
    {
        id: 'noise',
        term: 'Noise',
        aliases: ['white noise'],
        definition: "A hiss with no pitch, containing all frequencies at once. Mixed in, it adds breath to a pad or flute, the chiff of a pluck, or the body of a snare or wind sound.",
        seeAlso: ['mixer', 'oscillator']
    },
    {
        id: 'notch',
        term: 'Notch filter',
        aliases: ['band-reject', 'band-stop'],
        definition: "The opposite of a band-pass filter: it takes away a narrow band of frequencies around the cutoff and lets everything else through. Swept, it gives a hollow, phaser-like movement.",
        seeAlso: ['filter', 'band-pass', 'phaser']
    },
    {
        id: 'octave',
        term: 'Octave',
        definition: "The distance from one note to the next note of the same name, twelve semitones up or down. A note an octave higher is twice the frequency. Oscillators often have an octave or range switch, sometimes marked in feet (16′, 8′, 4′, after organ pipes; 8′ is normal pitch).",
        seeAlso: ['semitone', 'cent']
    },
    {
        id: 'oscillator',
        term: 'Oscillator',
        aliases: ['osc', 'VCO'],
        definition: "The part that makes the raw sound: a tone that repeats at the pitch of the note you play. Its waveform decides the basic character, which the rest of the synthesizer then shapes. Most synthesizers have two or more oscillators to mix and detune.",
        seeAlso: ['waveform', 'detune', 'mixer', 'sub-oscillator']
    },
    {
        id: 'oversampling',
        term: 'Oversampling',
        definition: "Working out the sound at a multiple of the sample rate, then bringing it back down. It reduces aliasing on bright sounds and high notes, at the cost of more processing power.",
        seeAlso: ['aliasing', 'sample-rate']
    },
    {
        id: 'pan',
        term: 'Pan',
        aliases: ['panning', 'stereo spread', 'spread'],
        definition: "Where a sound sits between the left and right speakers. Spreading voices or unison copies across the stereo field makes a sound wider.",
        seeAlso: ['unison']
    },
    {
        id: 'phaser',
        term: 'Phaser',
        definition: "An effect that sweeps a set of notches up and down the sound's frequencies, giving a gentle, swirling movement. It's softer than a flanger.",
        seeAlso: ['effects', 'flanger', 'chorus', 'notch']
    },
    {
        id: 'pitch-bend',
        term: 'Pitch bend',
        aliases: ['bend', 'pitch wheel'],
        definition: "Bending the pitch of the notes up or down as you play, usually with the wheel or lever beside the keys, which springs back to the middle. The bend range sets how far the full movement goes, often two semitones.",
        seeAlso: ['mod-wheel', 'semitone', 'mpe']
    },
    {
        id: 'plug-in',
        term: 'Plug-in',
        aliases: ['plugin', 'virtual instrument'],
        definition: "An instrument or effect that runs inside a DAW rather than on its own. It's installed in one or more formats (VST3, Audio Unit, AAX), and the DAW finds it on its next scan.",
        seeAlso: ['daw', 'vst3', 'au', 'aax', 'standalone']
    },
    {
        id: 'polyphonic',
        term: 'Polyphonic',
        aliases: ['poly', 'polyphony'],
        definition: "Playing several notes at once, so you can play chords. Polyphony is how many notes can sound together; play more than that and the oldest notes are cut short to make room (voice stealing).",
        seeAlso: ['monophonic', 'voice', 'unison']
    },
    {
        id: 'preset',
        term: 'Preset',
        aliases: ['patch', 'program', 'sound'],
        definition: "A saved sound: every setting of the instrument, stored under a name so you can call it back. Patch and program mean the same; patch comes from the cables that connected the parts of early modular synthesizers.",
        seeAlso: ['bank', 'init']
    },
    {
        id: 'probability',
        term: 'Probability',
        aliases: ['step probability', 'note probability', 'chance'],
        definition: "The chance that each step of a sequence, or each note of an arpeggio, actually plays. At 100% everything plays; lower, some notes drop out at random while the pattern keeps time, so a loop varies a little each time round instead of repeating exactly.",
        seeAlso: ['step-sequencer', 'arpeggiator']
    },
    {
        id: 'pulse-width',
        term: 'Pulse width',
        aliases: ['PW', 'PWM', 'pulse width modulation', 'duty cycle'],
        definition: "How much of each cycle a pulse wave spends up rather than down. At 50% it's a square wave, hollow and woody; narrower pulses sound thinner and more nasal. Moving the width with an LFO, called pulse width modulation or PWM, gives a rich, chorus-like shimmer.",
        seeAlso: ['waveform', 'lfo', 'modulation']
    },
    {
        id: 'release',
        term: 'Release',
        definition: "The last stage of an envelope: how long the sound takes to fade after you let go of the key. Short for tight, staccato notes; long for sounds that ring on and blend into the next chord.",
        seeAlso: ['adsr', 'sustain']
    },
    {
        id: 'resonance',
        term: 'Resonance',
        aliases: ['Q', 'emphasis', 'peak'],
        definition: "A boost a filter gives to the frequencies right at its cutoff. A little adds bite; a lot adds a whistling, vocal edge, most noticeable when the cutoff moves. At its highest, many filters ring on their own as a pure tone (self-oscillation).",
        seeAlso: ['filter', 'cutoff']
    },
    {
        id: 'reverb',
        term: 'Reverb',
        definition: "An effect that adds the reflections of a room, hall or plate, so a sound seems to be played in a space instead of right in your ear. Size or decay sets how big the space is and how long the tail lasts.",
        seeAlso: ['effects', 'delay', 'dry-wet']
    },
    {
        id: 'ring-modulator',
        term: 'Ring modulator',
        aliases: ['ring mod'],
        definition: "Multiplies two signals together, producing new frequencies that aren't in tune with the notes. The result is metallic and bell-like, or clangorous, depending on how the two are tuned.",
        seeAlso: ['oscillator', 'harmonics']
    },
    {
        id: 'sample-and-hold',
        term: 'Sample and hold',
        aliases: ['S&H', 'S/H', 'sample & hold'],
        definition: "A modulation that jumps to a new random level at regular moments and holds it until the next: a staircase rather than a smooth wave. Sent to a filter's cutoff in time with the tempo, it gives the bubbling, computer-like patterns of classic analog synthesizers. Many LFOs have it as a shape.",
        seeAlso: ['lfo', 'modulation', 'tempo-sync']
    },
    {
        id: 'sample-rate',
        term: 'Sample rate',
        definition: "How many times a second digital audio is measured, such as 44,100 or 48,000 (44.1 or 48 kHz). The DAW or the standalone app's audio settings choose it; the instrument works at whatever rate it's given.",
        seeAlso: ['buffer-size', 'aliasing', 'oversampling']
    },
    {
        id: 'semitone',
        term: 'Semitone',
        aliases: ['half step'],
        definition: "The smallest step between notes in Western music: from one key on a piano keyboard to the very next, black or white. Twelve make an octave.",
        seeAlso: ['octave', 'cent']
    },
    {
        id: 'slope',
        term: 'Slope',
        aliases: ['filter slope', 'poles', '12 dB', '24 dB'],
        definition: "How steeply a filter takes sound away beyond its cutoff, in decibels per octave. A 24 dB/octave (4-pole) filter cuts sharply and sounds fat and smooth; a 12 dB/octave (2-pole) one is gentler and lets more brightness through.",
        seeAlso: ['filter', 'cutoff']
    },
    {
        id: 'source',
        term: 'Source',
        aliases: ['modulation source'],
        definition: "What does the modulating: an LFO, an envelope, velocity, the mod wheel, aftertouch, a step sequencer, and so on. In a modulation matrix, each route has a source, a destination and an amount.",
        seeAlso: ['modulation-matrix', 'destination', 'modulation', 'bipolar']
    },
    {
        id: 'split',
        term: 'Split',
        aliases: ['key split', 'keyboard split', 'layer'],
        definition: "Dividing the keyboard at a key so the two halves play different sounds, such as a bass on the left and a lead or chords on the right. A layer is the opposite: two sounds on the same keys at once.",
        seeAlso: ['voice']
    },
    {
        id: 'standalone',
        term: 'Standalone app',
        aliases: ['standalone'],
        definition: "The instrument running as its own app, without a DAW. It's handy for practicing, auditioning presets, or playing live: choose your audio output and MIDI keyboard in its settings.",
        seeAlso: ['daw', 'plug-in', 'buffer-size']
    },
    {
        id: 'step-sequencer',
        term: 'Step sequencer',
        aliases: ['sequencer'],
        definition: "Plays a repeating pattern of steps in time with the tempo, each step with its own note (or chord) and settings. Unlike an arpeggiator, it plays what you've programmed rather than what you're holding. Many can play the steps in other orders too (backward, back and forth, or at random), and some let each step carry a modulation value as well, so the sequencer can move the filter or other controls in time with the pattern, with or without playing notes.",
        seeAlso: ['arpeggiator', 'gate', 'tempo-sync', 'probability', 'source']
    },
    {
        id: 'sub-oscillator',
        term: 'Sub oscillator',
        aliases: ['sub', 'sub osc'],
        definition: "An extra oscillator tuned one or two octaves below the main one, usually with a simple waveform. It adds weight and depth under a bass or lead without changing its character much.",
        seeAlso: ['oscillator', 'octave']
    },
    {
        id: 'sustain',
        term: 'Sustain',
        definition: "The third stage of an envelope: the level it holds for as long as the key stays down. Unlike attack, decay and release, it's a level, not a time. Also short for the sustain pedal, which holds notes after you lift your fingers, as on a piano.",
        seeAlso: ['adsr', 'decay', 'release']
    },
    {
        id: 'sync',
        term: 'Oscillator sync',
        aliases: ['hard sync', 'sync'],
        definition: "Making one oscillator restart its cycle every time another one does. The synced oscillator then follows the other's pitch, and changing its own tuning changes its tone instead, giving a tearing, vocal sound when swept.",
        seeAlso: ['oscillator', 'harmonics']
    },
    {
        id: 'tempo-sync',
        term: 'Tempo sync',
        aliases: ['host sync', 'sync to tempo'],
        definition: "Setting a time or rate in musical lengths (a quarter note, an eighth note, a dotted eighth) instead of seconds or hertz, so an LFO, delay, arpeggiator or sequencer follows the tempo and stays in time when it changes.",
        seeAlso: ['bpm', 'clock', 'lfo', 'delay']
    },
    {
        id: 'tremolo',
        term: 'Tremolo',
        definition: "A regular rise and fall in volume, usually from an LFO on the amplifier. Not to be confused with vibrato, which moves the pitch.",
        seeAlso: ['vibrato', 'lfo', 'amplifier']
    },
    {
        id: 'unison',
        term: 'Unison',
        definition: "Stacking several voices on each note, detuned and often spread in stereo, for a huge, thick sound. It uses up polyphony: four-voice unison on an eight-voice instrument leaves two notes at once.",
        seeAlso: ['detune', 'voice', 'polyphonic', 'pan']
    },
    {
        id: 'velocity',
        term: 'Velocity',
        definition: "How fast, so how hard, you strike a key, sent with every note as a number from 1 to 127. It usually makes notes louder, and can also make them brighter or change anything else you route it to.",
        seeAlso: ['aftertouch', 'midi', 'modulation']
    },
    {
        id: 'vibrato',
        term: 'Vibrato',
        definition: "A gentle, regular wavering of pitch, usually from an LFO, often brought in with the mod wheel. Not to be confused with tremolo, which moves the volume.",
        seeAlso: ['tremolo', 'lfo', 'mod-wheel']
    },
    {
        id: 'voice',
        term: 'Voice',
        definition: "Everything that makes one note: its oscillators, filter, amplifier and envelopes. An eight-voice instrument can play eight notes at once.",
        seeAlso: ['polyphonic', 'monophonic', 'unison']
    },
    {
        id: 'vst3',
        term: 'VST3',
        definition: "The plug-in format most DAWs on Windows, macOS and Linux use.",
        seeAlso: ['plug-in', 'au', 'aax', 'daw']
    },
    {
        id: 'waveform',
        term: 'Waveform',
        aliases: ['wave', 'wave shape', 'sine', 'sawtooth', 'saw', 'square', 'triangle', 'pulse'],
        definition: "The shape of an oscillator's cycle, which sets its basic tone. A sine is pure and soft; a triangle a little brighter, like a flute; a square hollow, like a clarinet; a sawtooth bright and buzzy, the starting point for brass and strings; a pulse thinner and more nasal as it narrows.",
        seeAlso: ['oscillator', 'harmonics', 'pulse-width']
    }
];
